import React, { createContext, useContext, useState, useEffect } from 'react';
import authAPI from '../api/auth';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if user is logged in on initial load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      try {
        setCurrentUser(JSON.parse(user));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);
  
  // Register a new user
  const register = async (userData) => {
    const response = await authAPI.register(userData);
    
    // Store token
    localStorage.setItem('auth_token', response.data.auth_token);
    
    return response;
  };
  
  // Login user
  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    
    // Store token
    localStorage.setItem('auth_token', response.data.auth_token);
    
    return response;
  };
  
  // Request WhatsApp verification code
  const requestWhatsAppCode = async (phoneNumber = null) => {
    return await authAPI.requestWhatsAppCode(phoneNumber);
  };
  
  // Verify WhatsApp code
  const verifyWhatsAppCode = async (code, phoneNumber = null) => {
    const response = await authAPI.verifyWhatsAppCode(code, phoneNumber);
    
    // Store user data
    const userData = response.data.user;
    setCurrentUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Update token if provided
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
    }
    
    return response;
  };
  
  // Request password reset
  const requestPasswordReset = async (email) => {
    return await authAPI.requestPasswordReset(email);
  };
  
  // Verify password reset code
  const verifyPasswordResetCode = async (email, code) => {
    const response = await authAPI.verifyPasswordResetCode(email, code);
    
    // Store token for password reset
    if (response.data.token) {
      localStorage.setItem('reset_token', response.data.token);
    }
    
    return response;
  };
  
  // Reset password
  const resetPassword = async (newPassword, token = null) => {
    const resetToken = token || localStorage.getItem('reset_token');
    
    if (!resetToken) {
      throw new Error('No reset token available');
    }
    
    const response = await authAPI.resetPassword(newPassword, resetToken);
    
    // Clean up reset token
    localStorage.removeItem('reset_token');
    
    // Store new auth token if provided
    if (response.data.auth_token) {
      localStorage.setItem('auth_token', response.data.auth_token);
    }
    
    return response;
  };
  
  // Logout user
  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setCurrentUser(null);
  };
  
  const value = {
    currentUser,
    loading,
    registerUser: register,
    login,
    logout,
    requestWhatsAppCode,
    verifyWhatsAppCode,
    requestPasswordReset,
    verifyPasswordResetCode,
    resetPassword
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
